/**
 * Foxhole "subregion" (control zone) helpers.
 *
 * A subregion is the influence area around a Town Base or Relic Base inside a
 * hex region. Visually it matches what players see in-game as the dashed
 * voronoi-like splits between captured towns. We approximate it with a true
 * Voronoi diagram over base positions, clipped to the hex polygon.
 */
import { Delaunay } from 'd3-delaunay';
import { HEX_VERTICES, TILE_W, TILE_H } from './foxhole-maps.js';

/**
 * War-API iconType values that designate a "base" eligible to define a
 * control subregion. Includes Town Bases (T1/T2/T3) and Relic Bases.
 *
 * Reference: https://github.com/clapfoot/warapi
 */
export const SUBREGION_BASE_ICON_TYPES: ReadonlySet<number> = new Set([
  45, 46, 47, // Relic Base 1/2/3
  56, 57, 58, // Town Base Tier 1/2/3
]);

/**
 * Faction colours as published by Clapfoot in the WarAPI documentation
 * (Hex SRGB values). Neutral uses a dark slate grey.
 */
export const SUBREGION_COLORS = {
  COLONIALS: '#516C4B',
  WARDENS: '#245682',
  NONE: '#3a3f4a',
} as const;

/** Fill opacity applied to every subregion cell (validated with user). */
export const SUBREGION_OPACITY = 0.35;

/** Stroke colour and opacity drawn around each cell to mimic the in-game borders. */
export const SUBREGION_STROKE = '#0b0d12';
export const SUBREGION_STROKE_OPACITY = 0.75;

export type FactionKey = keyof typeof SUBREGION_COLORS;

export function colorForTeamId(teamId: string | null | undefined): string {
  if (teamId === 'COLONIALS') return SUBREGION_COLORS.COLONIALS;
  if (teamId === 'WARDENS') return SUBREGION_COLORS.WARDENS;
  return SUBREGION_COLORS.NONE;
}

/** Single Voronoi cell ready to be rendered as an SVG <path>. */
export interface SubregionCell {
  /** SVG path data in tile-local coordinates (origin at tile centre). */
  d: string;
  /** CSS hex colour for the cell fill. */
  color: string;
}

/** Base input in tile-local coordinates. */
export interface SubregionBase {
  x: number;
  y: number;
  teamId: string | null | undefined;
}

const HEX_PATH_LOCAL = `M${HEX_VERTICES.map((p) => `${p.x},${p.y}`).join(' L')} Z`;

function pointsToPath(points: ArrayLike<number>): string {
  if (points.length < 4) return '';
  let d = `M${points[0]},${points[1]}`;
  for (let i = 2; i < points.length; i += 2) {
    d += ` L${points[i]},${points[i + 1]}`;
  }
  return `${d} Z`;
}

/**
 * Compute the Voronoi cells for the given bases, expressed in tile-local
 * coordinates (origin at tile centre). Returns an empty array when no bases
 * are supplied. Cells are bounded to the tile bounding box; finer clipping
 * to the actual hexagon is expected to be handled by the caller via an SVG
 * <clipPath>.
 */
export function computeSubregionCells(bases: readonly SubregionBase[]): SubregionCell[] {
  if (bases.length === 0) return [];

  if (bases.length === 1) {
    return [{ d: HEX_PATH_LOCAL, color: colorForTeamId(bases[0]!.teamId) }];
  }

  const points: number[] = [];
  for (const b of bases) {
    points.push(b.x, b.y);
  }

  const delaunay = new Delaunay(Float64Array.from(points));
  const voronoi = delaunay.voronoi([-TILE_W / 2, -TILE_H / 2, TILE_W / 2, TILE_H / 2]);

  const cells: SubregionCell[] = [];
  for (let i = 0; i < bases.length; i += 1) {
    const polygon = voronoi.cellPolygon(i);
    if (!polygon || polygon.length < 3) continue;
    const flat: number[] = [];
    for (const [x, y] of polygon) flat.push(x, y);
    const d = pointsToPath(flat);
    if (!d) continue;
    cells.push({ d, color: colorForTeamId(bases[i]!.teamId) });
  }
  return cells;
}
